import { useState, useCallback, useEffect } from "react";
import { toast } from "react-toastify";
import { OLITASK_SERVICE_BASE_URL } from "constants";
import apiClient from "utils/apiClient";

const API_BASE_URL = `${OLITASK_SERVICE_BASE_URL}/api/v1/donations`;

export default function useDonationsList() {
  const authToken = sessionStorage.getItem("authToken");
  const [donations, setDonations] = useState([]);
  const [selectedDonation, setSelectedDonation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // handle axios errors
  const handleAxiosErrors = (err, action = "operation") => {
    let message = "An unexpected error occurred.";
    if (err.response) {
      message =
        err.response.data?.message ||
        `Failed to ${action}: ${err.response.statusText}`;
    } else if (err.request) {
      message =
        "No response from the server. Please check your internet connection.";
    } else {
      message = err.message || "Something went wrong.";
    }
    setError(message);
    toast.error(message);
    console.error(`${action} error:`, err);
  };

  // Fetch all donations
  const fetchDonations = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const pageSize = 50;
      const firstResponse = await apiClient.get(
        `${API_BASE_URL}?page=0&size=${pageSize}`
      );

      const { content: firstContent = [], totalPages = 1 } = firstResponse.data;
      let allDonations = [...firstContent];

      if (totalPages > 1) {
        const promises = [];
        for (let p = 1; p < totalPages; p++) {
          promises.push(
            apiClient.get(`${API_BASE_URL}?page=${p}&size=${pageSize}`)
          );
        }

        const responses = await Promise.all(promises);
        responses.forEach((res) => {
          allDonations = allDonations.concat(res.data.content || []);
        });
      }

      setDonations(allDonations);
    } catch (err) {
      handleAxiosErrors(err, "Fetch donations");
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  // Fetch one donation by ID
  const fetchDonationById = useCallback(
    async (id) => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiClient.get(`${API_BASE_URL}/${id}`);
        setSelectedDonation(res.data);
        return res.data;
      } catch (err) {
        handleAxiosErrors(err, "fetch donation details");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authToken]
  );

  // Delete donation
  const deleteDonation = useCallback(
    async (id) => {
      setLoading(true);
      setError(null);
      try {
        await apiClient.delete(`${API_BASE_URL}/${id}`);

        // Remove deleted donation from local state
        setDonations((prev) => prev.filter((key) => key.id !== id));
        toast.success("Donation deleted successfully!", {
          theme: "colored",
        });
      } catch (err) {
        handleAxiosErrors(err, "Delete donation");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authToken]
  );

  // fetch automatically on mount
  useEffect(() => {
    if (authToken) fetchDonations();
  }, [authToken, fetchDonations]);

  return {
    donations,
    selectedDonation,
    loading,
    error,
    fetchDonations,
    fetchDonationById,
    deleteDonation,
    setSelectedDonation,
  };
}
